import React, {useEffect, useRef, useState} from 'react'
import {observer} from 'mobx-react-lite'
import {useNavigate} from 'react-router-dom'
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel, LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material'
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import Grid from '@mui/material/Grid2'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import SidebarToggleIcon from '../components/widget/SidebarToggleIcon'
import {GrantAIFeature} from '../../../entity/enums/grant-ai-feature'
import {toast} from 'react-toastify'
import {Form, Formik, FormikHelpers, FormikProps} from 'formik'
import {AgentSearchDto} from '../../../entity/dto/agent-search-dto'
import {useService} from '../../../infrastructure/service/service'
import {State} from '../../../entity/model/state'
import {County} from '../../../entity/model/county'
import {DropdownOption} from '../../../entity/core/dropdown-option'
import LoadingComponent from '../components/widget/LoadingComponent'
import {ScraperConfigResource} from '../../../entity/resource/scraper-config-resource'
import RefreshIcon from '@mui/icons-material/Refresh';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {UserPermission} from '../../../entity/enums/user-permission';
import AddFundDialog from '../components/marketplace/AddFundDialog';

// @ts-ignore
import chatIcon from '../../../assets/images/logo/chat-Icon.png'
import '../../../assets/css/style.css'
import '../../../assets/css/pages/grant-ai.css'

const GrantAI = (): JSX.Element => {
  const navigate = useNavigate();
  const {grantAIService, stateService, countyService, scraperConfigService, userService, auditLogsService, subscriptionService} = useService();

  const [messageInput, setMessageInput] = useState<string>('');
  const [isExpandedSidebar, setExpandedSidebar] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [agentMode, setAgentMode] = useState<boolean>(true);
  const [counties, setCounties] = useState<County[]>([]);
  const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
  const [countyOptions, setCountyOptions] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [scraperConfig, setScraperConfig] = useState<ScraperConfigResource>();
  const [trigger, setTrigger] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [progressRefresh, setProgressRefresh] = useState<boolean>(false);
  const [batchId, setBatchId] = useState<string>('');
  const [lastStateMessage, setLastStateMessage] = useState<string>('');
  const [tokenBalance, setTokenBalance] = useState<number>(0);
  const [addFundsPermission, setAddFundsPermission] = useState<boolean>(false);
  const [openAddFundsDialog, setOpenAddFundsDialog] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);
      messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});

      setProgress(0);
      setMessage('Your request is being processed');

      const user = await userService.getCurrentUser();

      setAddFundsPermission(userService.isPermitted(UserPermission.PURCHASE_TOKENS));

      if (user)
        await fetchTokenBalance(user.organizationID ?? '');

      const states = await stateService.getStates();
      setStateOptions(states.map((state: State) => ({key: state.id, text: state.shortName, value: state.id})));

      const counties = await countyService.getCounties();
      setCounties(counties);

      setLoading(false);
    }

    init();
  }, [])

  const fetchTokenBalance = async (organizationID: string): Promise<void> => {
    const balanceResponse = await subscriptionService.getTokenBalance(organizationID);
    if (balanceResponse)
      setTokenBalance(balanceResponse.balance);
  }

  const toggleSidebar = (): void => {
    setExpandedSidebar(!isExpandedSidebar);
  }

  const handleChatIconClick = (): void => {
    if (!isExpandedSidebar)
      setExpandedSidebar(true);
  }

  const handleMessageInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setMessageInput(event.target.value);
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setMenuAnchorEl(event.currentTarget);
  }

  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
  }

  const handleAgentMode = async (): Promise<void> => {
    setAgentMode(true);
    setLoading(true);

    const states = await stateService.getStates();
    setStateOptions(states.map((state: State) => ({key: state.id, text: state.shortName, value: state.id})));

    const counties = await countyService.getCounties();
    setCounties(counties);

    setLoading(false);
  }

  const handleValidate = (values: AgentSearchDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!values.state)
      errors.state = 'State is required.';

    if (!values.county)
      errors.county = 'County is required.';

    if (!values.parcelNumber)
      errors.parcelNumber = 'Parcel number is required.';

    return errors;
  }

  const handleStateChange = (handleChange: any) => (event: any) => {
    const stateId = event.target.value;
    const stateCounties = counties.filter((county: County) => county.stateID === stateId).sort((a: County, b: County) => a.name.localeCompare(b.name));
    setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));

    handleChange({target: {name: event.target.name, value: event.target.value}});
  }

  const handleCountyChange = (handleChange: any) => async (event: any) => {
    setLoading(true);

    const countyId = event.target.value;
    handleChange({target: {name: event.target.name, value: event.target.value}});

    if (countyId) {
      const config = await scraperConfigService.getScraperConfig(countyId);

      if (!config?.value.status)
        toast.warning(config?.value.message ?? 'Agent mode is not available for this county.');

      setScraperConfig(config);
    } else
      setScraperConfig(undefined);

    setLoading(false);
  }

  const handleAgentSubmit = async (values: AgentSearchDto, {setSubmitting}: FormikHelpers<AgentSearchDto>): Promise<void> => {
    setSubmitting(true);
    setTrigger(true);

    const currentUser = await userService.getCurrentUser();
    const batchId = crypto.randomUUID();

    grantAIService.executeAgent(currentUser?.organizationID ?? '', values.parcelNumber, batchId, scraperConfig?.value);

    setProgress(10);
    setMessage('Your request is being processed');

    await fetchProgress(batchId);
    setSubmitting(false);
  }

  const fetchProgress = async (batchId: string): Promise<void> => {
    let previousProgress = 0;
    let progressUnchangedDuration = 0;
    const maxUnchangedDuration = 20000;
    const interval = 10000;
    const maxUnchangedCycles = maxUnchangedDuration / interval;

    const intervalId = setInterval(async (): Promise<void> => {
      const currentUser = await userService.getCurrentUser();
      const response = await auditLogsService.getGrantAIAgentLogs(currentUser?.organizationID ?? '', batchId);
      if (response) {
        if (response.progress > progress)
          setProgress(response.progress);

        setMessage(response.message);

        if (response.progress === 90) {
          clearInterval(intervalId);
          
          await grantAIService.downloadArchive(response.data?.trimStart() ?? '', 'title_documents');
          setProgress(100);
          setMessage('Agent mode execution was successful');
          toast.success('Agent mode execution was successful !!');

          setTrigger(false);
          window.location.reload();
        }

        if (response.progress !== previousProgress) {
          previousProgress = response.progress;
          progressUnchangedDuration = 0;
        } else
          progressUnchangedDuration++;

        if (progressUnchangedDuration >= maxUnchangedCycles) {
          setProgressRefresh(true);
          setBatchId(batchId);
          setLastStateMessage(response.message);

          setMessage('Agent mode executing. Please refresh to check the progress.');
          toast.info('Agent mode executing. Please refresh to check the progress.');
          clearInterval(intervalId);
        }

      } else {
        setTrigger(false);
        clearInterval(intervalId);
      }
    }, interval);
  }

  const handleProgressRefresh = async (): Promise<void> => {
    setProgressRefresh(false);
    setMessage(lastStateMessage);
    setProgress((prevState: number): number => prevState + 1);

    await fetchProgress(batchId);
  }

  const handleMenuAction = async (action: GrantAIFeature): Promise<void> => {
    handleMenuClose()
    switch (action) {
    case GrantAIFeature.AGENT_MODE:
      await handleAgentMode();
      break;
    default:
      toast.info('Coming Soon ...');
      break;
    }
  }

  const handleAddTokens = (): void => {
    setOpenAddFundsDialog(true);
  }

  const handleSearchCountyRecords = (): void => {
    navigate('/marketplace-search');
  }

  // const handleMarketTitleServices = (): void => {
  //   navigate('/business-record-search');
  // }

  return (
    <Box className='page-container grant-ai-container'>
      <Box className='grant-ai-chat-box'>
        <Box className={`grant-ai-sidebar ${isExpandedSidebar ? 'expanded' : 'collapsed'}`}>
          <Box className='grant-ai-header-section'>
            <IconButton
              onClick={handleChatIconClick}
              className={`grant-ai-chat-icon ${isExpandedSidebar ? 'disabled' : ''}`}
              size='small'
              title={isExpandedSidebar ? '' : 'Expand sidebar'}>
              <img src={chatIcon} alt='Chat' className='chat-icon-image'/>
              {
                !isExpandedSidebar &&
                  <div className='chat-icon-hover-overlay'>
                    <SidebarToggleIcon/>
                  </div>
              }
            </IconButton>
            {
              isExpandedSidebar &&
                <Box className='grant-ai-close-container'>
                  <IconButton
                    onClick={toggleSidebar}
                    className='grant-ai-close-icon'
                    size='small'>
                    <SidebarToggleIcon/>
                  </IconButton>
                </Box>
            }
          </Box>
          <Box className='grant-ai-menu-section'>
            {
              <Box className='grant-ai-menu-item' onClick={handleSearchCountyRecords} >
                <IconButton className='grant-ai-menu-icon' size='medium'>
                  <SearchIcon fontSize='medium'/>
                </IconButton>
                {
                  isExpandedSidebar &&
                    <Typography className='grant-ai-menu-text'>Search County Records</Typography>
                }
              </Box>
            }
            {/* <Box className='grant-ai-menu-item' onClick={handleMarketTitleServices}>
              <IconButton className='grant-ai-menu-icon' size='medium'>
                <StoreIcon fontSize='medium'/>
              </IconButton>
              {
                isExpandedSidebar &&
                  <Typography className='grant-ai-menu-text'>Market & Request Title</Typography>
              }
            </Box> */}
            {/* <Box className='grant-ai-menu-item'>
              <IconButton className='grant-ai-menu-icon' size='medium' disabled={true}>
                <AttachMoneyIcon fontSize='medium'/>
              </IconButton>
              {
                isExpandedSidebar &&
                  <Typography className='grant-ai-menu-text grant-ai-menu-text-disabled'>Buy, Sell or Trade Title</Typography>
              }
            </Box> */}
          </Box>
          <Box className='grant-ai-sidebar-spacer'/>
        </Box>
        <Box className='grant-ai-main-content'>
          <Box className='grant-ai-header'>
            <Box className='grant-ai-header-content'>
              <Box className='grant-ai-title-section'>
                <Typography variant='h3' className='grant-ai-title'>Grant AI</Typography>
                <Typography variant='h6' className='grant-ai-subtitle'>AI-powered title report analysis and insights</Typography>
              </Box>
              <Box className='grant-ai-token-chip'>
                <Box className='token-chip-content'>
                  <Typography className='token-chip-icon'><ConfirmationNumberOutlinedIcon/></Typography>
                  <Typography className='token-chip-text'>Tokens:</Typography>
                  <Typography className='token-chip-value'>{tokenBalance}</Typography>
                </Box>
              </Box>
              {
                addFundsPermission &&
                  <Button
                    className="zitles-button submit add-token-button"
                    variant="outlined"
                    size="small"
                    onClick={handleAddTokens}>
                    Add Funds
                  </Button>
              }
            </Box>
          </Box>
          <Box className='grant-ai-messages-area'>
            <Box className='grant-ai-message-container'>
              <Box className='grant-ai-message ai-message'>
                <Typography variant='body1' className='grant-ai-message-text'>
                  Hello, I'm Grant AI, Your personal title agent. I can help you Jump-start your searches
                  by gathering documents. Select a State, County, and Enter a Parcel number to continue.
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box className='grant-ai-input-container'>
            {
              agentMode
                ? !trigger
                  ? <Box>
                      <Box className='grant-ai-agent-header'>
                        <Grid container spacing={2}>
                          <Grid size={2}>
                            <Box></Box>
                          </Grid>
                          <Grid size={10}>
                            <Typography variant='h6'>
                              Agent Mode - Title Document Search
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                      <Formik
                        initialValues={grantAIService.getInitialValues()}
                        validate={handleValidate}
                        onSubmit={handleAgentSubmit}>
                        {({values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting}: FormikProps<AgentSearchDto>): JSX.Element => (
                          <Form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                              <Grid size={2}>
                                <Box></Box>
                              </Grid>
                              <Grid size={6.5 / 3}>
                                <FormControl
                                  required
                                  fullWidth
                                  error={touched.state && Boolean(errors.state)}>
                                  <InputLabel>State</InputLabel>
                                  <Select
                                    id='input-agent-state'
                                    name='state'
                                    value={values.state}
                                    label='State'
                                    onBlur={handleBlur}
                                    onChange={handleStateChange(handleChange)}
                                    disabled={loading}>
                                    {
                                      stateOptions.map((option: DropdownOption): JSX.Element =>
                                        <MenuItem key={option.key} value={option.value}>
                                          {option.text}
                                        </MenuItem>
                                      )
                                    }
                                  </Select>
                                  <FormHelperText>{touched.state && errors.state}</FormHelperText>
                                </FormControl>
                              </Grid>
                              <Grid size={6.5 / 3}>
                                <FormControl
                                  required
                                  fullWidth
                                  error={touched.county && Boolean(errors.county)}>
                                  <InputLabel>County</InputLabel>
                                  <Select
                                    id='input-agent-county'
                                    name='county'
                                    value={values.county}
                                    label='County'
                                    onBlur={handleBlur}
                                    onChange={handleCountyChange(handleChange)}
                                    disabled={loading || !values.state}>
                                    {
                                      countyOptions.map((option: DropdownOption): JSX.Element =>
                                        <MenuItem key={option.key} value={option.value}>
                                          {option.text}
                                        </MenuItem>
                                      )
                                    }
                                  </Select>
                                  <FormHelperText>{touched.county && errors.county}</FormHelperText>
                                </FormControl>
                              </Grid>
                              <Grid size={6.5 / 3}>
                                <TextField
                                  id='input-agent-parcel'
                                  required
                                  fullWidth
                                  label='Parcel Number'
                                  name='parcelNumber'
                                  value={values.parcelNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  placeholder='Enter parcel number'
                                  variant='outlined'
                                  error={touched.parcelNumber && Boolean(errors.parcelNumber)}
                                  helperText={touched.parcelNumber && errors.parcelNumber}/>
                              </Grid>
                              <Grid size={1.5}>
                                {
                                  tokenBalance < 20 &&
                                      <Typography variant="body2" className="error-text insufficient-tokens-message">Insufficient Tokens</Typography>
                                }
                                <Button
                                  type='submit'
                                  variant='contained'
                                  disabled={isSubmitting || !scraperConfig?.value.status || tokenBalance < 20}
                                  className={`zitles-button submit ${!scraperConfig?.value.status || isSubmitting || tokenBalance < 20 ? 'disable' : ''}`}
                                  fullWidth>
                                  {isSubmitting ? 'Searching...' : 'Search title docs'}
                                </Button>
                              </Grid>
                              <Grid size={2}>
                                <Box></Box>
                              </Grid>
                            </Grid>
                          </Form>
                        )}
                      </Formik>
                      <Box className='grant-ai-agent-footer'>
                        <Grid container spacing={2}>
                          <Grid size={2}>
                            <Box></Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  : <Box className={'progress-zone'}>
                      <Box className={'progress-icon-zone'}>
                        {
                          progressRefresh
                            ? <RefreshIcon className={'progress-icon'} cursor={'pointer'} onClick={handleProgressRefresh}/>
                            : <InsertDriveFileIcon className={'progress-icon'} />
                        }
                      </Box>
                      <Box className={'progress-bar-zone'}>
                        <Typography className={'progress-bar-title'}>{message}</Typography>
                        <Box className={'progress-bar-container'}>
                          <Box className={'progress-bar'}>
                            <LinearProgress variant='determinate' value={progress} color='success' className={'progress'}/>
                          </Box>
                          <Typography className={'progress-value'}>{`${progress}%`}</Typography>
                        </Box>
                      </Box>
                    </Box>
                : <Box className='grant-ai-input-wrapper'>
                    <IconButton
                      className='grant-ai-plus-button'
                      size='medium'
                      onClick={handleMenuOpen}
                      aria-controls={Boolean(menuAnchorEl) ? 'plus-menu' : undefined}
                      aria-haspopup='true'
                      aria-expanded={Boolean(menuAnchorEl) ? 'true' : undefined}>
                      <AddIcon/>
                    </IconButton>
                    <Menu
                      id='plus-menu'
                      anchorEl={menuAnchorEl}
                      open={Boolean(menuAnchorEl)}
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                      }}
                      transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                      }}>
                      {
                        Object.values(GrantAIFeature).map((feature) => {
                          const isAgentMode = feature === GrantAIFeature.AGENT_MODE;
                          return (
                            <MenuItem
                              className={!isAgentMode ? 'coming-soon-menu-item' : ''}
                              key={feature}
                              onClick={isAgentMode ? () => handleMenuAction(feature) : undefined}
                              disabled={!isAgentMode}>
                              <ListItemText>{isAgentMode ? feature : `${feature} - (Coming Soon)`}</ListItemText>
                            </MenuItem>
                          );
                        })
                      }
                    </Menu>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      disabled={true}
                      placeholder='Type your message...'
                      value={messageInput}
                      onChange={handleMessageInputChange}
                      variant='outlined'
                      className='grant-ai-input-field'/>
                    <Button
                      variant='contained'
                      disabled={true}
                      className='zitles-button submit grant-ai-send-button disable'>
                      Send
                    </Button>
                  </Box>
            }
          </Box>
        </Box>
      </Box>
      {
        openAddFundsDialog &&
          <AddFundDialog open={openAddFundsDialog} setOpen={setOpenAddFundsDialog}/>
      }
      {
        loading && <LoadingComponent/>
      }
    </Box>
  );
}

export default observer(GrantAI);
